/**
 * Admin Module Routes
 */

import { lazy } from "react";
import { ModuleRoutes } from "../types";

// Lazy load components
const UserManagement = lazy(() => import("@/pages/admin/UserManagement"));
const PortalTelasGlobalPage = lazy(() => import("@/pages/admin/PortalTelasGlobalPage"));
const ChatConfigPage = lazy(() => import("@/pages/admin/ChatConfigPage"));
const UserDetailPage = lazy(() => import("@/pages/admin/UserDetailPage"));
const RoleManagement = lazy(() => import("@/pages/admin/RoleManagement"));
const PermissionManagement = lazy(() => import("@/pages/admin/PermissionManagement"));
const FAQAdminPanel = lazy(() => import("@/pages/admin/FAQAdminPanel"));
const GlossaryAdminPage = lazy(() => import("@/pages/admin/GlossaryAdminPage"));
const ImportWizardPage = lazy(() => import("@/pages/admin/ImportWizardPage"));
const MaintenancePanel = lazy(() => import("@/pages/admin/MaintenancePanel"));
const EmergencyPanel = lazy(() => import("@/modules/maintenance/pages/EmergencyPanel"));
const SupportDashboard = lazy(() => import("@/modules/maintenance/pages/SupportDashboard"));
const MaintenanceHistory = lazy(() => import("@/modules/maintenance/pages/MaintenanceHistory"));
const ErrosSistemaPage = lazy(() => import("@/pages/admin/ErrosSistemaPage"));
const AnaliseIAErrosPage = lazy(() => import("@/pages/admin/AnaliseIAErrosPage"));
const AIProvidersPage = lazy(() => import("@/pages/admin/AIProvidersPage"));
const StorageConfiguracaoPage = lazy(() => import("@/pages/admin/StorageConfiguracaoPage"));
const CredenciaisAwsBiometriaPage = lazy(() => import("@/pages/admin/CredenciaisAwsBiometriaPage"));
const DriveConfigPage = lazy(() => import("@/pages/admin/DriveConfigPage"));
const EventBusPage = lazy(() => import("@/pages/admin/EventBusPage"));
const LgpdDashboard = lazy(() => import("@/pages/admin/LgpdDashboard"));
const BitrixImportWizard = lazy(() => import("@/pages/admin/migration/BitrixImportWizard"));
const BatchReportPage = lazy(() => import("@/pages/admin/migration/BatchReportPage"));
const CutoverDashboardPage = lazy(() => import("@/pages/admin/migration/CutoverDashboardPage"));
const AdminWhatsappPassthroughPage = lazy(
  () => import("@/pages/admin/AdminWhatsappPassthroughPage")
);
const SentimentDashboard = lazy(() => import("@/pages/admin/SentimentDashboard"));
const SplitKeyRecordingPage = lazy(() => import("@/pages/admin/SplitKeyRecordingPage"));
const RecordingsPage = lazy(() => import("@/pages/admin/RecordingsPage"));
const ApresentacaoPage = lazy(() => import("@/pages/admin/ApresentacaoPage"));
const AntiFraudReportPage = lazy(() => import("@/pages/admin/AntiFraudReportPage"));
const PartnerOnboardingPage = lazy(() => import("@/pages/admin/PartnerOnboardingPage"));
const MarketplacePage = lazy(() => import("@/pages/admin/MarketplacePage"));
const AutomationsPage = lazy(() => import("@/pages/admin/AutomationsPage"));
const BrandingPage = lazy(() => import("@/pages/admin/BrandingPage"));
const AudioVideoInfrastructurePage = lazy(
  () => import("@/pages/admin/AudioVideoInfrastructurePage")
);

export const adminRoutes: ModuleRoutes = {
  moduleId: "admin",
  basePath: "/admin",
  routes: [
    {
      path: "/admin/usuarios",
      element: <UserManagement />,
      permission: { resource: "users", action: "manage" },
    },
    {
      // C1b — nível de acesso das telas do portal, padrão para todos os
      // parceiros. Mesma permissão da configuração por parceiro.
      path: "/admin/portal-telas",
      element: <PortalTelasGlobalPage />,
      permission: { resource: "substabelecidos", action: "manage" },
    },
    {
      // Opções do chat que valem para todos (hoje: o cartão do site nos links). Mesma
      // permissão do `PUT /chat/configuracoes` — só o papel Administrator a tem.
      path: "/admin/chat",
      element: <ChatConfigPage />,
      permission: { resource: "chat_config", action: "manage" },
    },
    {
      // Rota que antes caía em 404 (reportado via errorTracker). Mantida sob
      // a mesma permissão da listagem — ver admin.routes.tsx:/admin/usuarios.
      path: "/admin/usuarios/:id",
      element: <UserDetailPage />,
      permission: { resource: "users", action: "manage" },
    },
    {
      path: "/admin/perfis",
      element: <RoleManagement />,
      permission: { resource: "roles", action: "manage" },
    },
    {
      path: "/admin/permissoes",
      element: <PermissionManagement />,
      permission: { resource: "permissions", action: "manage" },
    },
    {
      path: "/admin/import-wizard",
      element: <ImportWizardPage />,
      permission: { resource: "admin", action: "access" },
    },
    {
      path: "/admin/maintenance/history",
      element: <MaintenanceHistory />,
      permission: { resource: "maintenance", action: "view" },
    },
    {
      path: "/admin/maintenance/support",
      element: <SupportDashboard />,
      permission: { resource: "maintenance", action: "view" },
    },
    {
      path: "/admin/maintenance/emergency",
      element: <EmergencyPanel />,
      permission: { resource: "maintenance", action: "activate" },
    },
    {
      path: "/admin/maintenance",
      element: <MaintenancePanel />,
      permission: { resource: "maintenance", action: "activate" },
    },
    // Alias PT-BR (Fix MEGA #2, 2026-05-17)
    {
      path: "/admin/manutencao",
      element: <MaintenancePanel />,
      permission: { resource: "maintenance", action: "activate" },
    },
    {
      path: "/admin/erros-sistema",
      element: <ErrosSistemaPage />,
      permission: { resource: "system_logs", action: "read" },
    },
    {
      path: "/admin/erros-sistema/ia",
      element: <AnaliseIAErrosPage />,
      permission: { resource: "system_logs", action: "read" },
    },
    {
      path: "/admin/ai-providers",
      element: <AIProvidersPage />,
      permission: { resource: "system_logs", action: "read" },
    },
    {
      // Backend valida `admin:storage:manage` via require_permissions, que faz
      // split(":", 1) — então a permissão fica resource="admin",
      // action="storage:manage" (vide missing_admin_permissions_seed.py).
      path: "/admin/storage",
      element: <StorageConfiguracaoPage />,
      permission: { resource: "admin", action: "storage:manage" },
    },
    {
      // Credenciais AWS da biometria facial (spec 2026-09-23). `biometria:configurar`
      // é a MESMA permissão que o backend exige nas quatro rotas de
      // /biometria/credenciais-aws e que o item do menu pede — só Administrator.
      path: "/admin/biometria/credenciais-aws",
      element: <CredenciaisAwsBiometriaPage />,
      permission: { resource: "biometria", action: "configurar" },
    },
    {
      // As alavancas do Drive: quem cria pasta, e a área pessoal.
      //
      // ⚠️ Usa `documentos:manage` — a MESMA permissão que o backend exige em
      //    `PUT /drive/config` e que define quem é "administrador do Drive" na
      //    resolução de acesso. Uma permissão diferente aqui abriria a tela
      //    para quem o backend recusaria ao salvar.
      path: "/admin/drive",
      element: <DriveConfigPage />,
      permission: { resource: "documentos", action: "manage" },
    },
    {
      // Convenção do seed `event_bus_permissions_seed.py:38` é diferente do
      // storage: resource = "admin:event_bus" literal (com colon), action =
      // "read"|"manage". A rota match exato.
      path: "/admin/event-bus",
      element: <EventBusPage />,
      permission: { resource: "admin:event_bus", action: "read" },
    },
    {
      // LGPD DPO dashboard (Story 4.4.5). Reusa permissão `lgpd:dpo` criada
      // na Story 4.4.4 (Admin/Administrator já têm; partner_owner nunca).
      path: "/admin/lgpd",
      element: <LgpdDashboard />,
      permission: { resource: "lgpd", action: "dpo" },
    },
    {
      // Wizard de migração Bitrix24 (PRD 5, Story 5.2.2). Permissão
      // `migration:execute` — o wizard DISPARA migração, não só lê;
      // criada pelo seed `migration_permissions_seed.py` (Story 5.2.1).
      path: "/admin/migration/bitrix-import",
      element: <BitrixImportWizard />,
      permission: { resource: "migration", action: "execute" },
    },
    {
      // Relatório detalhado de um lote de migração Bitrix24 (PRD 5, Story 5.2.4).
      // Permissão `migration:read` — esta página só LÊ um batch (diferente do
      // wizard, que é `migration:execute` porque DISPARA migração). O backend
      // `GET /batches/{id}` exige `migration:read`. O UserMappingTable montado
      // aqui tem uma ação de escrita (`POST /mappings/{id}/edit`, que exige
      // `migration:execute`) — mas isso é gate por endpoint no backend; quem
      // tem só `read` vê o relatório inteiro, e o POST do picker dá 403.
      path: "/admin/migration/batches/:id",
      element: <BatchReportPage />,
      permission: { resource: "migration", action: "read" },
    },
    {
      // Cutover dashboard (PRD 5, Story 5.3.3, ADR-507). Permissão `read` pra
      // ver adoption + estado; o backend gate'a `disable-mirror` /
      // `enable-mirror` por `migration:execute` separadamente. Quem tem só
      // `read` vê o dashboard mas não consegue clicar o botão de desligar
      // (chamada cai em 403, dialog mostra o erro).
      path: "/admin/migration/cutover-status",
      element: <CutoverDashboardPage />,
      permission: { resource: "migration", action: "read" },
    },
    {
      // WhatsApp passthrough — admin overview (PRD 5, Story 5.4.1, ADR-508).
      // Permissão `atendimentos:read` espelha o endpoint per-partner em
      // `/atendimentos/passthrough/{id}` (Story 4.7.x) — operador que gerencia
      // atendimentos já tem. `disable` exige `atendimentos:update` no backend.
      path: "/admin/whatsapp-passthrough",
      element: <AdminWhatsappPassthroughPage />,
      permission: { resource: "atendimentos", action: "read" },
    },
    {
      // Sentiment dashboard (PRD 6, Story 6.3.3). Read-only — lista parceiros
      // com risk_score agregado dos últimos N dias + drill-down em sessões.
      // Mesma permissão `atendimentos:read` que `/admin/whatsapp-passthrough`:
      // supervisor/admin/agent já têm; ninguém de fora vê.
      path: "/admin/sentiment-dashboard",
      element: <SentimentDashboard />,
      permission: { resource: "atendimentos", action: "read" },
    },
    {
      // Split-key recordings (PRD 6, Story 6.5.3). UI guiada de
      // contribuição/reconstrução Shamir T-of-N. Gate de página por
      // `split_key:read` — TI/Manager/Admin/Administrator já têm via seed
      // 6.5.2. Botões internos (contribute/reconstruct) também gated em
      // runtime; backend valida via require_permissions.
      path: "/admin/split-key",
      element: <SplitKeyRecordingPage />,
      permission: { resource: "split_key", action: "read" },
    },
    {
      // Marketplace de templates (PRD 6, Story 6.6.4). Catálogo cross-tenant
      // de workflows/RICE/scripts/exports/broadcasts. Gate por
      // `marketplace:read` (Admin/Administrator/Manager/Operator têm via seed
      // 6.6.3). Botões `Importar`/`Avaliar` dentro do dialog são gated por
      // `marketplace:import` / `marketplace:rate`.
      path: "/admin/marketplace",
      element: <MarketplacePage />,
      permission: { resource: "marketplace", action: "read" },
    },
    {
      path: "/admin/automations",
      element: <AutomationsPage />,
      permission: { resource: "notifications", action: "rules:manage" },
    },
    {
      path: "/admin/branding",
      element: <BrandingPage />,
      permission: { resource: "admin", action: "branding:manage" },
    },
    {
      path: "/admin/audio-video/infrastructure",
      element: <AudioVideoInfrastructurePage />,
      permission: { resource: "audio_video_admin", action: "read" },
    },
    {
      path: "/admin/recordings",
      element: <RecordingsPage />,
      permission: { resource: "calls", action: "read" },
    },
    {
      path: "/admin/apresentacao",
      element: <ApresentacaoPage />,
      permission: { resource: "admin", action: "access" },
    },
    {
      path: "/admin/anti-fraud",
      element: <AntiFraudReportPage />,
      permission: { resource: "admin", action: "access" },
    },
    {
      path: "/admin/partner-onboarding",
      element: <PartnerOnboardingPage />,
      permission: { resource: "admin", action: "access" },
    },
  ],
};

// Ajuda admin routes (separate to keep modularity)
export const ajudaAdminRoutes: ModuleRoutes = {
  moduleId: "ajuda-admin",
  basePath: "/ajuda",
  routes: [
    {
      path: "/ajuda/faq/gerenciar",
      element: <FAQAdminPanel />,
      permission: { resource: "faq", action: "gerenciar" },
    },
    {
      path: "/ajuda/glossario/gerenciar",
      element: <GlossaryAdminPage />,
      permission: { resource: "glossario", action: "gerenciar" },
    },
  ],
};
